/**
 * The three views of docs/08 §3.3 and docs/12 M5, as a filter over one scene.
 *
 * The api sends the bounded neighbourhood once; each view is a subset of it, so
 * switching views costs no request and no round trip. Keeping the filter pure
 * (and here, rather than inside the renderer) is what lets the "does the worked
 * example render correctly in all three views" exit criterion be a test.
 */

import type { PersonSummary, TreeEdge, TreeScene } from '@famtree/shared';

export const TREE_VIEWS = ['family', 'ancestors', 'descendants'] as const;
export type TreeView = (typeof TREE_VIEWS)[number];

export interface SceneSubset {
  focus: string;
  persons: PersonSummary[];
  edges: TreeEdge[];
}

/** Parent → children and child → parents, over drawable parent edges only. */
function adjacency(edges: TreeEdge[]): {
  parents: Map<string, string[]>;
  children: Map<string, string[]>;
  partners: Map<string, string[]>;
} {
  const parents = new Map<string, string[]>();
  const children = new Map<string, string[]>();
  const partners = new Map<string, string[]>();
  for (const edge of edges) {
    if (edge.kind === 'parent_child') {
      if (edge.parent_basis === 'step') continue;
      parents.set(edge.b, [...(parents.get(edge.b) ?? []), edge.a]);
      children.set(edge.a, [...(children.get(edge.a) ?? []), edge.b]);
    } else if (edge.kind === 'union') {
      partners.set(edge.a, [...(partners.get(edge.a) ?? []), edge.b]);
      partners.set(edge.b, [...(partners.get(edge.b) ?? []), edge.a]);
    }
  }
  return { parents, children, partners };
}

export function selectView(scene: TreeScene, view: TreeView, generations: number): SceneSubset {
  const { parents, children, partners } = adjacency(scene.edges);
  const keep = new Set<string>([scene.focus]);

  const climb = (from: string, step: Map<string, string[]>, depth: number): void => {
    let layer = [from];
    for (let level = 0; level < depth && layer.length > 0; level += 1) {
      const next: string[] = [];
      for (const node of layer) {
        for (const other of step.get(node) ?? []) {
          if (keep.has(other)) continue;
          keep.add(other);
          next.push(other);
        }
      }
      layer = next;
    }
  };

  switch (view) {
    case 'ancestors':
      // A pedigree: the focus and the lines above them. Each ancestor's partner
      // comes too, or half of every couple would be missing from the chart.
      climb(scene.focus, parents, generations);
      break;
    case 'descendants':
      climb(scene.focus, children, generations);
      break;
    case 'family':
      // §3.3's family view: the focus, their partners, and the children of each
      // union — one household, not a lineage.
      for (const partner of partners.get(scene.focus) ?? []) keep.add(partner);
      for (const child of children.get(scene.focus) ?? []) keep.add(child);
      for (const parent of parents.get(scene.focus) ?? []) keep.add(parent);
      break;
  }

  // Every view gains the partners of everyone it shows: a couple drawn as one
  // person is a chart that has lost the household it is trying to describe.
  for (const node of [...keep]) {
    for (const partner of partners.get(node) ?? []) keep.add(partner);
  }

  const persons = scene.persons.filter((person) => keep.has(person.id));
  const edges = scene.edges.filter((edge) => keep.has(edge.a) && keep.has(edge.b));
  return { focus: scene.focus, persons, edges };
}
